import {
  Create,
  SimpleForm,
  TextInput,
  useRefresh,
  useNotify,
  useRedirect,
  SaveButton,
  Button,
  Toolbar,
  useGetIdentity,
  useDataProvider,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Box,
  Typography,
  CircularProgress,
  TableRow,
  TableCell,
} from '@mui/material'
import { useEffect, useCallback, useState } from 'react'
import TagsInput from '../components/VendorTagsInput'
import VendorFormLayout from '../components/VendorFormLayout'

// Define the form section type
interface FormField {
  label: string
  source: string
  required: boolean
}

interface FormSection {
  title: string
  fields: FormField[]
}

interface RawInputProps {
  source: string
  disabled?: boolean
  required?: boolean
}

const CreateToolbar = (props: any) => {
  const redirect = useRedirect()

  const handleCancel = useCallback(() => {
    redirect('list', 'vendors')
  }, [redirect])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleCancel])

  return (
    <Toolbar {...props}>
      <SaveButton />
      <Button label="Cancel" onClick={handleCancel} />
    </Toolbar>
  )
}

const RawInput = ({
  source,
  disabled = false,
  required = false,
}: RawInputProps) => {
  const { register, control } = useFormContext()
  const value = useWatch({ name: source, control })
  return (
    <input
      {...register(source, { required })}
      value={value ?? ''}
      disabled={disabled}
      style={{
        width: '100%',
        height: '32px',
        border: '1px solid #ccc',
        padding: '2px 6px',
        fontSize: '14px',
        backgroundColor: disabled ? '#f5f5f5' : 'white',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s',
      }}
      onFocus={(e) => {
        e.currentTarget.style.border = '1px solid #4da3ff'
      }}
      onBlur={(e) => {
        e.currentTarget.style.border = '1px solid #ccc'
      }}
    />
  )
}

const FieldRow = ({
  label,
  source,
  required,
}: {
  label: string
  source: string
  required?: boolean
}) => {
  const { control } = useFormContext()
  const value = useWatch({ name: source, control })
  const isMissingRequired = required && (value === '' || value == null)

  return (
    <TableRow key={source}>
      <TableCell
        sx={{
          bgcolor: '#d9d9d9',
          width: '35%',
          p: 0.25,
          textAlign: 'left',
          verticalAlign: 'middle',
        }}
      >
        {label}
        {isMissingRequired && (
          <span style={{ color: 'red', marginLeft: 4 }}>*</span>
        )}
      </TableCell>
      <TableCell sx={{ p: 0.25 }}>
        <RawInput source={source} required={required} />
      </TableCell>
    </TableRow>
  )
}

const VendorCreate = () => {
  const refresh = useRefresh()
  const notify = useNotify()
  const redirect = useRedirect()
  const dataProvider = useDataProvider()
  // State to store the form sections
  const [formSections, setFormSections] = useState<FormSection[]>([])
  const [loading, setLoading] = useState(true)

  // Load the organisation's vendor form configuration
  useEffect(() => {
    const loadVendorForm = async () => {
      try {
        setLoading(true)
        const { data } = await dataProvider.custom(
          'organisations/current/vendor-forms',
          { method: 'GET' }
        )

        // Check if we got a list of vendor forms or a single vendor form
        if (Array.isArray(data)) {
          // We got a list of vendor forms, use the first one if available
          if (data.length > 0) {
            setFormSections(data[0].vendor_form || [])
          } else {
            // Use empty form sections if no vendor forms exist
            setFormSections([])
          }
        } else if (data?.vendor_form) {
          // We got a single vendor form
          setFormSections(data.vendor_form)
        } else {
          // Use empty form sections if no configuration is found
          setFormSections([])
        }
      } catch (error) {
        console.error('Error loading vendor form:', error)
        notify('Error loading vendor form configuration', { type: 'error' })
        // Use empty form sections on error
        setFormSections([])
      } finally {
        setLoading(false)
      }
    }

    loadVendorForm()
  }, [dataProvider, notify])

  const transform = (data: any) => {
    const tagIds = (data.tags ?? []).map((t: any) => t.id)

    let specialFields: { source: string; value: string }[] = []
    let vendorName = data.name // Default to the name field if no field with source "name" is found

    // Use the loaded form sections to create special fields
    specialFields = formSections
      .flatMap((section) => section.fields)
      .map(({ label, source }) => {
        // If this is the field with source "name", use its value as the vendor name
        if (source === 'name' && data[source]) {
          vendorName = data[source]
          // Still include it in specialFields to maintain the tag association
        }
        return {
          source,
          value: data[source] ?? '',
        }
      })
      .filter((tag) => tag.value !== '') // Keep all non-empty fields, including "name"

    return {
      name: vendorName,
      tags: [...tagIds, ...specialFields],
      territory_id: 1,
    }
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Create
      transform={transform}
      mutationOptions={{
        onSuccess: () => {
          notify('Vendor created', { type: 'success' })
          refresh()
          redirect('list', 'vendors')
        },
        onError: (error: any) => {
          let errorMessage = 'Failed to create vendor'
          if (error instanceof Error) {
            errorMessage = error.message
          }
          notify(`${errorMessage}`, { type: 'error' })
        },
      }}
    >
      <SimpleForm toolbar={<CreateToolbar />}>
        {formSections && formSections.length > 0 ? (
          <VendorFormLayout
            formSections={formSections}
            renderField={(field) => (
              <RawInput source={field.source} required={field.required} />
            )}
          />
        ) : (
          <>
            <Box
              mb={0}
              py={1.5}
              px={2}
              bgcolor="#0077c8"
              color="white"
              width="100%"
              display="block"
            >
              <Typography variant="h6">Vendor Basic Information</Typography>
            </Box>
            <TextInput source="name" label="Vendor Name" fullWidth required />
          </>
        )}

        <TagsInput
          source="tags"
          // Filter out tags that are already represented in the form fields
          filter={(tag) => {
            // If no form sections, show all tags
            if (!formSections || formSections.length === 0) return true

            // Get all sources from form sections
            const formSources = formSections
              .flatMap((section) => section.fields)
              .map((field) => field.source)

            // If tag has a category and its name matches a form source, filter it out
            return !(
              tag.tag_category && formSources.includes(tag.tag_category.name)
            )
          }}
        />
      </SimpleForm>
    </Create>
  )
}

export default VendorCreate
